import { Dropdown, Tooltip } from 'antd';
import { NavLink } from 'react-router-dom';
import { 
  SettingOutlined, 
  UserOutlined, 
  ShoppingOutlined, 
  ShoppingCartOutlined, 
  CreditCardOutlined, 
  LogoutOutlined,
  MessageOutlined
} from '@ant-design/icons';

import { useAuth, useOrders } from '@hooks';
import './styles.css';

interface UserMenuProps {
  profile?: any;
}

export const UserMenu = ({ profile }: UserMenuProps) => {
  const { logout } = useAuth();
  const { createPortalSession } = useOrders();

  const hasStripeCustomer = !!profile?.stripeCustomerId;

  const handleAccessBillingPortal = async () => {
    if (!hasStripeCustomer) return;
    try {
      const response = await createPortalSession.mutateAsync();
      window.location.href = response.url;
    } catch (error) {
      // Error message handled by hook
    }
  };

  const avatar = profile?.physicalPerson?.avatar;
  const name = profile?.physicalPerson?.name;
  const email = profile?.email;

  const getInitial = () => {
    if (name) return name.charAt(0).toUpperCase();
    if (email) return email.charAt(0).toUpperCase();
    return '?';
  };

  return (
    <div className='flex items-center justify-center'>
      <Dropdown
        overlayClassName="user-menu-dropdown"
        menu={{
          items: [
            // Account Section
            {
              key: 'account-header',
              label: <div className="user-menu-section-header">Account</div>,
              disabled: true,
            },
            {
              key: 'settings',
              icon: <SettingOutlined className="user-menu-icon" />,
              label: <NavLink to='/settings' className="user-menu-item font-displayed !text-xl">Settings</NavLink>,
            },
            // Admin Section - only show if user is admin
            ...(profile?.profile === 'admin' ? [
              {
                type: 'divider' as const,
              },
              {
                key: 'admin-header',
                label: <div className="user-menu-section-header">Administration</div>,
                disabled: true,
              },
              {
                key: 'users',
                icon: <UserOutlined className="user-menu-icon" />,
                label: <NavLink to='/users' className="user-menu-item font-displayed !text-xl">Users</NavLink>,
              },
              {
                type: 'divider' as const,
              },
              {
                key: 'commerce-header',
                label: <div className="user-menu-section-header">Shop</div>,
                disabled: true,
              },
              {
                key: 'products',
                icon: <ShoppingOutlined className="user-menu-icon" />,
                label: <NavLink to='/products' className="user-menu-item font-displayed !text-xl">Products</NavLink>,
              },
              {
                key: 'orders',
                icon: <ShoppingCartOutlined className="user-menu-icon" />,
                label: <NavLink to='/orders' className="user-menu-item font-displayed !text-xl">Orders</NavLink>,
              },
              {
                key: 'billing',
                icon: <CreditCardOutlined className="user-menu-icon" />,
                disabled: !hasStripeCustomer,
                label: hasStripeCustomer ? (
                  <div onClick={handleAccessBillingPortal} className="user-menu-item font-displayed !text-xl">
                    Billing Portal
                  </div>
                ) : (
                  <Tooltip title="Available after your first purchase" placement="left">
                    <div className="text-gray-400 cursor-not-allowed user-menu-item font-displayed !text-xl">
                      Billing Portal
                    </div>
                  </Tooltip>
                ),
              },
              {
                key: 'testimonials',
                icon: <MessageOutlined className="user-menu-icon" />,
                label: <NavLink to='/testimonials' className="user-menu-item font-displayed !text-xl">Testimonials</NavLink>,
              },
            ] : []),
            // Divider before logout
            {
              type: 'divider' as const,
            },
            // Logout Section
            {
              key: 'logout',
              icon: <LogoutOutlined className="user-menu-icon" />,
              label: <span className='user-menu-item font-displayed !text-xl'>Logout</span>,
              onClick: () => logout(),
            },
          ],
        }}
        placement='bottomRight'
        trigger={['click']}
      >
        <div className='w-10 h-10 bg-white rounded-none border-2 border-black cursor-pointer overflow-hidden transition-all hover:border-gray-600 hover:shadow-sm group'>
          {avatar ? (
            <img
              src={avatar}
              alt={name || email}
              className='w-full h-full object-cover'
            />
          ) : (
            <div className='w-full h-full flex items-center justify-center'>
              <span className='text-black group-hover:text-[#F74902] text-lg font-orator font-bold leading-[0] select-none transition-colors duration-200 flex items-center justify-center'>{getInitial()}</span>
            </div>
          )}
        </div>
      </Dropdown>
    </div>
  );
};
